// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: one interleaved channel order at any depth (nested-ordering-plan.md section 3). The sidebar, the
// move maths, the Cast tree, every picker and the server all order channels through this builder, and the
// Obyrus Dart port runs the same JSON fixtures (fixtures/channel-tree). Exclude from upstream sync.

import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {THREAD_CHANNEL_TYPES, THREAD_ONLY_CHANNEL_TYPES} from '@fluxer/constants/src/ThreadConstants';

/** A technical cap only, so walks and payloads stay bounded. There is no product limit on nesting. */
export const MAX_CHANNEL_TREE_DEPTH = 32;
export const CHANNEL_PATH_SEPARATOR = ' > ';
const PATH_ELLIPSIS = '…';

export interface ChannelTreeChannel<Id extends string | bigint = string> {
	id: Id;
	type: number;
	parentId?: Id | null | undefined;
	position?: number | null | undefined;
	name?: string | null | undefined;
}

export type ChannelTreeIssueKind = 'unknown-parent' | 'non-category-parent' | 'cycle' | 'depth-limit';

export interface ChannelTreeIssue<Id extends string | bigint = string> {
	kind: ChannelTreeIssueKind;
	id: Id;
}

export interface ChannelTreeNode<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>> {
	channel: Channel;
	id: Id;
	/** 0 at the root. Exact, never capped: renderers cap the indent. */
	depth: number;
	/** The parent this channel is shown under: its parent_id when that names a category whose chain ends at the root. */
	effectiveParentId: Id | null;
	/** Channels and subcategories directly inside, in sibling order. Empty for anything but a category. */
	children: Array<ChannelTreeNode<Id, Channel>>;
	/** Position in the depth-first `nodes` list. */
	index: number;
	/** Effective ancestors, root first. */
	ancestorIds: ReadonlyArray<Id>;
}

export interface ChannelTree<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>> {
	roots: Array<ChannelTreeNode<Id, Channel>>;
	/** Every node depth-first: a category is followed immediately by its whole subtree. */
	nodes: Array<ChannelTreeNode<Id, Channel>>;
	byId: Map<Id, ChannelTreeNode<Id, Channel>>;
	issues: Array<ChannelTreeIssue<Id>>;
}

const DIGITS = /^\d+$/;

export function isThreadChannelType(type: number): boolean {
	return THREAD_CHANNEL_TYPES.has(type);
}

/** Text, announcement, link and thread-only (forum, media) channels: what the old sidebar bucketed before voice. */
export function isTextLikeChannelType(type: number): boolean {
	return (
		type === ChannelTypes.GUILD_TEXT ||
		type === ChannelTypes.GUILD_ANNOUNCEMENT ||
		type === ChannelTypes.GUILD_LINK ||
		THREAD_ONLY_CHANNEL_TYPES.has(type)
	);
}

/**
 * Ids compared as snowflakes: by number, so an 18-digit id sorts before a 19-digit one. Digit-only strings are
 * compared by length, then by code unit; anything else (test ids) by code unit.
 */
export function compareChannelIds(a: string | bigint, b: string | bigint): number {
	if (typeof a === 'bigint' && typeof b === 'bigint') {
		return compareOrdered(a, b);
	}
	const left = String(a);
	const right = String(b);
	if (left.length !== right.length && DIGITS.test(left) && DIGITS.test(right)) {
		return left.length - right.length;
	}
	return compareOrdered(left, right);
}

function compareOrdered<T extends string | bigint>(a: T, b: T): number {
	if (a < b) return -1;
	return a > b ? 1 : 0;
}

/** Sibling order: position ascending, then id as a snowflake. No type rank. */
export function compareChannelSiblings(
	a: {id: string | bigint; position?: number | null | undefined},
	b: {id: string | bigint; position?: number | null | undefined},
): number {
	const aPos = a.position ?? 0;
	const bPos = b.position ?? 0;
	if (aPos !== bPos) return aPos - bPos;
	return compareChannelIds(a.id, b.id);
}

/**
 * Builds the guild's channel tree. Pure and iterative.
 *
 * Threads are skipped. Nothing else is ever dropped: a channel whose parent is unknown, not a category, or
 * caught in a cycle (or whose chain runs into one) is shown at the root, ordered among the root siblings, and
 * reported once in `issues`.
 */
export function buildGuildChannelTree<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	channels: ReadonlyArray<Channel>,
): ChannelTree<Id, Channel> {
	const byChannelId = new Map<Id, Channel>();
	for (const channel of channels) {
		if (isThreadChannelType(channel.type)) continue;
		byChannelId.set(channel.id, channel);
	}
	const isCategoryId = (id: Id): boolean => byChannelId.get(id)?.type === ChannelTypes.GUILD_CATEGORY;
	// Whether a category's chain of category parents ends at the root, memoised so the whole pass is linear.
	const terminates = new Map<Id, boolean>();
	const chainTerminates = (startId: Id): boolean => {
		const path: Array<Id> = [];
		const onPath = new Set<Id>();
		let current: Id | null = startId;
		let result = true;
		while (current !== null) {
			const known = terminates.get(current);
			if (known !== undefined) {
				result = known;
				break;
			}
			if (onPath.has(current)) {
				result = false;
				break;
			}
			onPath.add(current);
			path.push(current);
			const next: Id | null = byChannelId.get(current)?.parentId ?? null;
			current = next !== null && isCategoryId(next) ? next : null;
		}
		for (const id of path) terminates.set(id, result);
		return result;
	};

	const issues: Array<ChannelTreeIssue<Id>> = [];
	const nodeById = new Map<Id, ChannelTreeNode<Id, Channel>>();
	for (const channel of byChannelId.values()) {
		nodeById.set(channel.id, {
			channel,
			id: channel.id,
			depth: 0,
			effectiveParentId: null,
			children: [],
			index: -1,
			ancestorIds: [],
		});
	}
	const roots: Array<ChannelTreeNode<Id, Channel>> = [];
	for (const channel of byChannelId.values()) {
		const node = nodeById.get(channel.id)!;
		const parentId = channel.parentId ?? null;
		let effectiveParentId: Id | null = null;
		if (parentId !== null) {
			if (!byChannelId.has(parentId)) {
				issues.push({kind: 'unknown-parent', id: channel.id});
			} else if (!isCategoryId(parentId)) {
				issues.push({kind: 'non-category-parent', id: channel.id});
			} else if (!chainTerminates(parentId)) {
				issues.push({kind: 'cycle', id: channel.id});
			} else {
				effectiveParentId = parentId;
			}
		}
		node.effectiveParentId = effectiveParentId;
		if (effectiveParentId === null) {
			roots.push(node);
		} else {
			nodeById.get(effectiveParentId)!.children.push(node);
		}
	}
	const bySiblingOrder = (a: ChannelTreeNode<Id, Channel>, b: ChannelTreeNode<Id, Channel>) =>
		compareChannelSiblings(a.channel, b.channel);
	roots.sort(bySiblingOrder);
	for (const node of nodeById.values()) {
		if (node.children.length > 1) node.children.sort(bySiblingOrder);
	}

	const nodes: Array<ChannelTreeNode<Id, Channel>> = [];
	const stack: Array<{node: ChannelTreeNode<Id, Channel>; depth: number; ancestorIds: ReadonlyArray<Id>}> = [];
	for (let i = roots.length - 1; i >= 0; i--) stack.push({node: roots[i], depth: 0, ancestorIds: []});
	while (stack.length > 0) {
		const {node, depth, ancestorIds} = stack.pop()!;
		node.depth = depth;
		node.ancestorIds = ancestorIds;
		node.index = nodes.length;
		nodes.push(node);
		if (depth > MAX_CHANNEL_TREE_DEPTH) issues.push({kind: 'depth-limit', id: node.id});
		if (node.children.length === 0) continue;
		const childAncestors = [...ancestorIds, node.id];
		for (let i = node.children.length - 1; i >= 0; i--) {
			stack.push({node: node.children[i], depth: depth + 1, ancestorIds: childAncestors});
		}
	}
	issues.sort((a, b) => compareChannelIds(a.id, b.id) || compareOrdered(a.kind, b.kind));
	return {roots, nodes, byId: nodeById, issues};
}

export interface FlattenChannelTreeOptions<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>> {
	/** Per node only: a category left out still lets its descendants through. */
	include?: (node: ChannelTreeNode<Id, Channel>) => boolean;
	/** Categories whose descendants are skipped. */
	collapsed?: ReadonlySet<Id>;
}

/** The depth-first list, optionally filtered. */
export function flattenChannelTree<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	tree: ChannelTree<Id, Channel>,
	options: FlattenChannelTreeOptions<Id, Channel> = {},
): Array<ChannelTreeNode<Id, Channel>> {
	const {include, collapsed} = options;
	if (!include && (!collapsed || collapsed.size === 0)) return [...tree.nodes];
	const result: Array<ChannelTreeNode<Id, Channel>> = [];
	for (const node of tree.nodes) {
		if (collapsed && collapsed.size > 0 && node.ancestorIds.some((ancestorId) => collapsed.has(ancestorId))) continue;
		if (include && !include(node)) continue;
		result.push(node);
	}
	return result;
}

/** Shorthand: the channels of `channels` in tree order. */
export function sortChannelsByTree<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	channels: ReadonlyArray<Channel>,
): Array<Channel> {
	return buildGuildChannelTree<Id, Channel>(channels).nodes.map((node) => node.channel);
}

/** Everything under `categoryId` at any depth, in tree order. Empty for an unknown id or a channel. */
export function channelSubtreeIds<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	tree: ChannelTree<Id, Channel>,
	categoryId: Id,
	options: {includeCategories?: boolean} = {},
): Array<Id> {
	const includeCategories = options.includeCategories ?? true;
	const root = tree.byId.get(categoryId);
	if (!root) return [];
	const ids: Array<Id> = [];
	for (let i = root.index + 1; i < tree.nodes.length; i++) {
		const node = tree.nodes[i];
		if (node.depth <= root.depth) break;
		if (!includeCategories && node.channel.type === ChannelTypes.GUILD_CATEGORY) continue;
		ids.push(node.id);
	}
	return ids;
}

/** The ordered children of a parent (null: the root). */
export function channelTreeSiblings<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	tree: ChannelTree<Id, Channel>,
	parentId: Id | null,
): Array<ChannelTreeNode<Id, Channel>> {
	if (parentId === null) return tree.roots;
	return tree.byId.get(parentId)?.children ?? [];
}

export interface ChannelPathOptions {
	separator?: string;
	/** Shortened from the left with "… > " so the immediate parent stays visible. */
	maxChars?: number;
	/** false: only the ancestors ("Parent > Child" of the categories above), for rows that show the name already. */
	includeSelf?: boolean;
}

/** "Parent > Child": the effective ancestors' names, then the channel's own. Empty when there is nothing to show. */
export function channelPathLabel<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	tree: ChannelTree<Id, Channel>,
	id: Id,
	options: ChannelPathOptions = {},
): string {
	const separator = options.separator ?? CHANNEL_PATH_SEPARATOR;
	const includeSelf = options.includeSelf ?? true;
	const node = tree.byId.get(id);
	if (!node) return '';
	const segments = node.ancestorIds.map((ancestorId) => tree.byId.get(ancestorId)?.channel.name ?? '');
	if (includeSelf) segments.push(node.channel.name ?? '');
	return formatChannelPath(segments, {separator, maxChars: options.maxChars, keep: includeSelf ? 2 : 1});
}

/**
 * Joins path segments with " > ", shortened from the left with "… > " beyond `maxChars`, never dropping the last
 * `keep` segments (the immediate parent, and the channel when it is included).
 */
export function formatChannelPath(
	segments: ReadonlyArray<string>,
	options: {separator?: string; maxChars?: number; keep?: number} = {},
): string {
	const separator = options.separator ?? CHANNEL_PATH_SEPARATOR;
	if (segments.length === 0) return '';
	let label = segments.join(separator);
	const maxChars = options.maxChars;
	if (maxChars === undefined || label.length <= maxChars) return label;
	const keep = options.keep ?? 1;
	let start = 0;
	while (segments.length - start > keep) {
		start++;
		label = `${PATH_ELLIPSIS}${separator}${segments.slice(start).join(separator)}`;
		if (label.length <= maxChars) break;
	}
	return label;
}

/**
 * Where a NEW child goes among a parent's ordered children (Q1: the default spot only, never enforced on moves).
 * Text-like: directly after the last text-like child, else before the first voice child, else at the end.
 * Voice, categories and anything else: at the end.
 */
export function defaultInsertionIndex(siblings: ReadonlyArray<{type: number}>, type: number): number {
	if (!isTextLikeChannelType(type)) return siblings.length;
	for (let i = siblings.length - 1; i >= 0; i--) {
		if (isTextLikeChannelType(siblings[i].type)) return i + 1;
	}
	const firstVoice = siblings.findIndex((sibling) => sibling.type === ChannelTypes.GUILD_VOICE);
	return firstVoice === -1 ? siblings.length : firstVoice;
}

/** The canonical guild-wide stored positions for an order: id -> index + 1. */
export function canonicalPositions<Id extends string | bigint>(orderedIds: ReadonlyArray<Id>): Map<Id, number> {
	const positions = new Map<Id, number>();
	orderedIds.forEach((id, index) => positions.set(id, index + 1));
	return positions;
}

export interface CategoryDeletionPlan<Id extends string | bigint> {
	/** The parent the direct children move to (null: the root). */
	newParentId: Id | null;
	/** The direct children, in their current order: they take the deleted category's place among its siblings. */
	movedChildIds: Array<Id>;
	/** The depth-first order without the category. Deeper descendants keep their parents. */
	orderedIds: Array<Id>;
}

/**
 * Deleting a category moves its direct children (channels and subcategories) up one level into the category's
 * place, in their current order, with no type re-sorting (Q4: no). Its subtree is contiguous right after it in
 * the depth-first list, so the new order is simply that list without the category.
 */
export function planCategoryDeletion<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	tree: ChannelTree<Id, Channel>,
	categoryId: Id,
): CategoryDeletionPlan<Id> | null {
	const node = tree.byId.get(categoryId);
	if (!node || node.channel.type !== ChannelTypes.GUILD_CATEGORY) return null;
	return {
		newParentId: node.effectiveParentId,
		movedChildIds: node.children.map((child) => child.id),
		orderedIds: tree.nodes.filter((candidate) => candidate.id !== categoryId).map((candidate) => candidate.id),
	};
}

/** The height of a subtree: 0 for a leaf. Used with MAX_CHANNEL_TREE_DEPTH to refuse moves past the cap. */
export function channelSubtreeHeight<Id extends string | bigint, Channel extends ChannelTreeChannel<Id>>(
	tree: ChannelTree<Id, Channel>,
	id: Id,
): number {
	const root = tree.byId.get(id);
	if (!root) return 0;
	let height = 0;
	for (let i = root.index + 1; i < tree.nodes.length; i++) {
		const node = tree.nodes[i];
		if (node.depth <= root.depth) break;
		height = Math.max(height, node.depth - root.depth);
	}
	return height;
}
